/**
 * Response handling utilities to standardize API response processing
 * and eliminate response.data vs response.data.data confusion
 */

/**
 * Safely extract array data from API response
 * Handles multiple response formats:
 * - Direct array: response = [...]
 * - Wrapped array: response = { data: [...] }
 * - Nested array: response = { data: { data: [...] } }
 * - With pagination: response = { data: [...], pagination: {...} }
 *
 * @param {Object|Array} response - API response
 * @param {Array} defaultValue - Default value if no array found (default: [])
 * @returns {Array} Extracted array data
 */
export const extractArrayData = (response, defaultValue = []) => {
  // Handle null/undefined response
  if (!response) return defaultValue;

  // Direct array response
  if (Array.isArray(response)) return response;

  // Check for nested data.data pattern first (most specific)
  if (response.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }

  // Check for data array pattern
  if (Array.isArray(response.data)) {
    return response.data;
  }

  // Check for direct array properties (games, users, etc.)
  const arrayProps = [
    "games",
    "users",
    "transactions",
    "notifications",
    "providers",
    "categories",
  ];
  for (const prop of arrayProps) {
    if (Array.isArray(response[prop])) {
      return response[prop];
    }
    if (Array.isArray(response.data?.[prop])) {
      return response.data[prop];
    }
  }

  // Return default if no array found
  return defaultValue;
};

/**
 * Safely extract pagination data from API response
 * @param {Object} response - API response
 * @param {Object} defaultValue - Default pagination object
 * @returns {Object} Pagination data
 */
export const extractPaginationData = (response, defaultValue = {}) => {
  if (!response) return defaultValue;

  // Check for pagination in various locations
  return (
    response.pagination ||
    response.data?.pagination ||
    response.meta?.pagination ||
    defaultValue
  );
};

/**
 * Safely extract count/total from API response
 * @param {Object} response - API response
 * @param {number} fallbackCount - Fallback to array length if provided
 * @returns {number} Count value
 */
export const extractCount = (response, fallbackCount = 0) => {
  if (!response) return fallbackCount;

  // Check various count properties
  const countProps = ["count", "total", "resultsCount", "totalCount"];

  for (const prop of countProps) {
    if (typeof response[prop] === "number") return response[prop];
    if (typeof response.data?.[prop] === "number") return response.data[prop];
    if (typeof response.meta?.[prop] === "number") return response.meta[prop];
  }

  return fallbackCount;
};

/**
 * Safely extract single object data from API response
 * @param {Object} response - API response
 * @param {Object} defaultValue - Default value if no object found
 * @returns {Object} Extracted object data
 */
export const extractObjectData = (response, defaultValue = {}) => {
  if (!response) return defaultValue;

  // If response.data exists and is an object (not array), return it
  if (
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    return response.data;
  }

  // Otherwise return the response itself (minus metadata)
  const { pagination, meta, ...data } = response;
  return Object.keys(data).length > 0 ? data : defaultValue;
};

/**
 * Standardized response processor for common API patterns
 * @param {Object|Array} response - API response
 * @param {Object} options - Processing options
 * @param {string} options.type - Expected data type: 'array', 'object', 'count'
 * @param {*} options.defaultValue - Default value if extraction fails
 * @returns {*} Processed response data
 */
export const processResponse = (response, options = {}) => {
  const { type = "array", defaultValue } = options;

  switch (type) {
    case "array":
      return extractArrayData(response, defaultValue || []);
    case "object":
      return extractObjectData(response, defaultValue || {});
    case "count":
      return extractCount(response, defaultValue || 0);
    default:
      return response;
  }
};

/**
 * Enhanced response processor that returns structured data
 * @param {Object|Array} response - API response
 * @returns {Object} Structured response with data, pagination, and count
 */
export const processStructuredResponse = (response) => {
  return {
    data: extractArrayData(response),
    pagination: extractPaginationData(response),
    count: extractCount(response, extractArrayData(response).length),
  };
};
